import { FC } from 'react';
import { DIALOGS_TABLE_NAME, type ConversationListItem } from '@/entities/conversations';
import { PresetActions, SelectionSaveOffer, SystemPresetCopyOffer, TablePresetsPopover } from '@/features/TablePresets';
import { useDialogsSection } from '@/pages/dialogs/model/useDialogsSection';
import { PRESETS_WIDTH_RESERVE_CLASS } from './ContentSearch/controlRowClasses';
import { DialogListDataGrid } from './DialogListDataGrid';
import { useDialogListState } from './model/useDialogListState';
import { usePresetSelection } from './model/usePresetSelection';

/** Пропсы секции списка диалогов */
export type DialogListSectionProps = {
  onLoaded?: (data: ConversationListItem[]) => void;
  /**
   * Узел вне колонки списка, в который переносится блок управления. Задают раскладки, где
   * рядом со списком стоит карточка диалога и колонке списка блоку тесно. Промежуток от
   * перенесённого блока до списка держит раскладка — по метке плотности на самом блоке.
   */
  controlsContainer?: HTMLElement | null;
  className?: string;
};

/**
 * Секция списка диалогов раздела «Диалоги» (и закреплённого пресета) на базе DataGrid.
 * Состояние списка ведёт `useDialogListState`; своё у раздела — слой отбора: пресеты таблицы
 * и выборка перехода с дашборда.
 */
export const DialogListSection: FC<DialogListSectionProps> = ({ className, controlsContainer, onLoaded }) => {
  // Раздел «Диалоги» и раздел каждого закреплённого пресета ведут свой вид списка: настройки,
  // пресет и слой выборки берутся из записи того раздела, в котором список открыт.
  const { sectionKey } = useDialogsSection(DIALOGS_TABLE_NAME);

  const { gridProps, presets } = useDialogListState({ sectionKey, onLoaded });

  // Выбор пресета в списке: закреплённый ведёт в свой раздел, незакреплённый — отбор «Диалогов».
  const { onPresetSelect, onPresetSaved, canClearActive } = usePresetSelection();

  return (
    <DialogListDataGrid
      {...gridProps}
      className={className}
      controlsContainer={controlsContainer}
      canSaveLayer={presets.canSaveActivePreset}
      layerName={presets.activePreset?.name}
      onSaveLayer={presets.onSaveActivePreset}
      canExitLayer={presets.canDeselectSelection}
      onExitLayer={presets.onDeselectSelection}
      layerBlock={
        <>
          <TablePresetsPopover
            sectionKey={sectionKey}
            widthReserveClassName={PRESETS_WIDTH_RESERVE_CLASS}
            onPresetSelect={onPresetSelect}
            selectionDescription={presets.selectionDescription}
            onPresetSaved={onPresetSaved}
          />
          {/* Управление применённым пресетом — рядом с его кнопкой, а не в строках списка */}
          <PresetActions sectionKey={sectionKey} onPresetSelect={onPresetSelect} canClearActive={canClearActive} />
        </>
      }
      // Предложения сохранить вид — под таблицей, а не над ней: копию системного пресета система
      // предлагает после первой же правки, обычно посреди настройки колонок, и строка над таблицей
      // сдвинула бы панель под курсором пользователя.
      footerSlot={
        <>
          <SelectionSaveOffer
            sectionKey={sectionKey}
            selection={presets.unsavedSelection}
            selectionDescription={presets.selectionDescription}
            onDismiss={presets.onSelectionHintDismiss}
            onPresetSaved={onPresetSaved}
          />
          <SystemPresetCopyOffer
            sectionKey={sectionKey}
            selectionDescription={presets.selectionDescription}
            onPresetSaved={onPresetSaved}
          />
        </>
      }
    />
  );
};
